/**
 * Input → intent mapping (pure; tested in test/commands.test.ts). The server validates
 * everything again; these checks only drive the UI (valid/invalid tint, greyed buttons).
 */
import { activeAllies } from '@sa/protocol';
import { BALANCE, buildCost, getGeo, landHexAt, type BuildKind, type EntityView, type HexIdx, type Intent, type PlayerView, type Vec2, type WorldMap } from '@sa/sim';
import { posNow } from '../net/interp';

/** Build strip order; hotkeys 1–7. */
export const BUILD_ORDER: readonly BuildKind[] = ['radar', 'sam', 'silo', 'city', 'airbase', 'sub', 'carrier'];

export const BUILD_LABEL: Record<BuildKind, string> = {
  radar: 'RADAR',
  sam: 'SAM',
  silo: 'SILO',
  city: 'CITY',
  airbase: 'AIRBASE',
  sub: 'SUB',
  carrier: 'CARRIER',
};

/** Range ring shown while placing (mu). */
export function placementRange(kind: BuildKind): number {
  switch (kind) {
    case 'radar':
      return BALANCE.vision.radar;
    case 'sam':
      return BALANCE.sam.range;
    case 'city':
      return BALANCE.vision.city;
    case 'carrier':
      return BALANCE.carrier.radar;
    default:
      return 0;
  }
}

export type PlaceError = 'notOwnHex' | 'occupied' | 'radiation' | 'spacing' | 'notPort' | 'cap' | 'credits' | 'badHex';

const MAX_RAD_NIBBLE = Math.floor(BALANCE.build.maxRad * 15 - 1e-9);

/** Own entities of `kind` (including ones under construction). */
export function ownedCount(view: PlayerView, kind: BuildKind): number {
  let n = 0;
  for (const e of view.entities.values()) if (e.owner === view.me && e.kind === kind) n++;
  return n;
}

export function capOf(kind: BuildKind): number {
  return kind === 'city' ? Infinity : BALANCE[kind].cap;
}

/** Is the build strip button usable (affordable and under the cap)? */
export function canAfford(view: PlayerView, kind: BuildKind): boolean {
  return view.credits >= buildCost(kind) && ownedCount(view, kind) < capOf(kind);
}

/** Client-side placement check for `kind` on land hex `hex`. */
export function checkPlacement(view: PlayerView, world: WorldMap, kind: BuildKind, hex: HexIdx): PlaceError | null {
  if (hex < 0 || hex >= world.landCount) return 'badHex';
  if (view.owner[hex] !== view.me) return 'notOwnHex';
  const ship = kind === 'sub' || kind === 'carrier';
  if (ship && (!world.coastal[hex] || world.portWater[hex] < 0)) return 'notPort';
  if (!ship) {
    if (view.rad[hex] > MAX_RAD_NIBBLE) return 'radiation';
    const geo = getGeo(world);
    for (const e of view.entities.values()) {
      if (e.kind !== 'city' && e.kind !== 'radar' && e.kind !== 'sam' && e.kind !== 'silo' && e.kind !== 'airbase') continue;
      const h = landHexAt(geo, e.x, e.y);
      if (h === hex) return 'occupied';
      if (kind === 'city' && e.kind === 'city' && axial(world, h, hex) < BALANCE.city.minSpacing) return 'spacing';
    }
  }
  if (ownedCount(view, kind) >= capOf(kind)) return 'cap';
  if (view.credits < buildCost(kind)) return 'credits';
  return null;
}

export const isShipKind = (k: EntityView['kind'] | BuildKind): k is 'sub' | 'carrier' => k === 'sub' || k === 'carrier';

/** Our ports: own coastal hexes a sub or carrier can be built at (it spawns on `wx, wy`). */
export function ownPorts(view: PlayerView, world: WorldMap): { hex: HexIdx; wx: number; wy: number }[] {
  const out: { hex: HexIdx; wx: number; wy: number }[] = [];
  for (let h = 0; h < world.landCount; h++) {
    const wh = world.portWater[h];
    if (view.owner[h] === view.me && world.coastal[h] && wh >= 0) out.push({ hex: h, wx: world.wcx[wh], wy: world.wcy[wh] });
  }
  return out;
}

/**
 * Placing a ship: the port nearest to (x, y) — measured to the hex and to the water the ship
 * spawns on, so pointing at the sea off our coast works — within `maxMu`; −1 if none.
 */
export function nearestPort(view: PlayerView, world: WorldMap, x: number, y: number, maxMu: number): HexIdx {
  let best = -1;
  let bd = maxMu * maxMu;
  for (const p of ownPorts(view, world)) {
    const d = Math.min((world.cx[p.hex] - x) ** 2 + (world.cy[p.hex] - y) ** 2, (p.wx - x) ** 2 + (p.wy - y) ** 2);
    if (d < bd) {
      bd = d;
      best = p.hex;
    }
  }
  return best;
}

function axial(w: WorldMap, a: HexIdx, b: HexIdx): number {
  if (a < 0 || b < 0) return Infinity;
  const dq = w.q[a] - w.q[b];
  const dr = w.r[a] - w.r[b];
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/** Launchers among the selection that can fire now. */
export function readyLaunchers(view: PlayerView, selected: readonly EntityView[]): EntityView[] {
  if (!view.released) return [];
  return selected.filter((e) => {
    if (e.owner !== view.me || (e.kind !== 'silo' && e.kind !== 'sub') || e.state === 'building') return false;
    const d = e.detail;
    if (!d || (d.loaded ?? 0) <= 0) return false;
    return (d.cooldownUntil ?? 0) <= view.tick;
  });
}

/** One launch per ready launcher at (x, y). Never targets known allied land. */
export function launchIntents(view: PlayerView, world: WorldMap, selected: readonly EntityView[], x: number, y: number): Intent[] {
  const h = landHexAt(getGeo(world), x, y);
  if (h >= 0 && activeAllies(view).includes(view.owner[h])) return [];
  const out: Intent[] = [];
  for (const e of readyLaunchers(view, selected)) {
    if (e.kind === 'sub' && !inSlbmRange(e, view.tick, x, y)) continue;
    out.push({ type: 'launch', launcherId: e.id, x, y });
  }
  return out;
}

/** Can sub `e` (where it is at `tick`, under way or not) reach (x, y) with an SLBM? */
export function inSlbmRange(e: EntityView, tick: number, x: number, y: number): boolean {
  const p: Vec2 = posNow(e, tick);
  return Math.hypot(p.x - x, p.y - y) <= BALANCE.sub.slbmRange;
}

/** Our finished ships among the selection (they take move orders). */
export function selectedShips(view: PlayerView, selected: readonly EntityView[]): EntityView[] {
  return selected.filter((e) => e.owner === view.me && isShipKind(e.kind) && e.state !== 'building');
}

/**
 * Right-click: move selected ships (`queue`, i.e. Shift: add a waypoint after their route),
 * retarget airborne aircraft; otherwise push focus on the clicked land hex (or clear it on water).
 */
export function rightClickIntents(view: PlayerView, selected: readonly EntityView[], x: number, y: number, hex: HexIdx, queue = false): Intent[] {
  const movers = selectedShips(view, selected);
  if (movers.length) return movers.map((e) => (queue ? { type: 'move', unitId: e.id, x, y, queue: true } : { type: 'move', unitId: e.id, x, y }));
  const air = selected.filter(
    (e) => e.owner === view.me && (e.kind === 'bomber' || e.kind === 'fighter') && e.detail?.phase !== 'parked' && e.state !== 'building',
  );
  if (air.length) return air.map((e) => ({ type: 'airCmd', aircraftId: e.id, cmd: 'retarget', x, y }));
  if (hex < 0) return view.focus === null ? [] : [{ type: 'focus', hex: null }];
  return hex === view.focus ? [] : [{ type: 'focus', hex }];
}

/** Aircraft of `kind` parked at `baseId` (ready to fly). */
export function parkedAt(view: PlayerView, baseId: number, kind: 'fighter' | 'bomber'): number {
  let n = 0;
  for (const e of view.entities.values()) if (e.owner === view.me && e.kind === kind && e.detail?.baseId === baseId && e.detail.phase === 'parked') n++;
  return n;
}

/**
 * A strike / patrol / scout click: one aircraft from every selected airbase / carrier in range with
 * one ready; refused with the reason otherwise (nothing ready, out of range, an ally's land). The
 * confirmation names the flight time from the nearest of them.
 */
export function airOrder(
  view: PlayerView,
  world: WorldMap,
  selected: readonly EntityView[],
  mission: 'strike' | 'patrol' | 'scout',
  x: number,
  y: number,
): { intents: Intent[]; confirm: string } | { refuse: string } {
  const kind = mission === 'strike' ? 'bomber' : 'fighter';
  const name = kind.toUpperCase();
  const { radius, speed } = BALANCE[kind];
  const ready = selected.filter((e) => e.owner === view.me && (e.kind === 'airbase' || e.kind === 'carrier') && parkedAt(view, e.id, kind) > 0);
  if (!ready.length) return { refuse: `NO ${name} READY` };
  if (mission === 'strike') {
    const h = landHexAt(getGeo(world), x, y);
    if (h >= 0 && activeAllies(view).includes(view.owner[h])) return { refuse: 'CANNOT TARGET AN ALLY' };
  }
  const reach = ready.map((b) => ({ b, d: Math.hypot(b.x - x, b.y - y) })).filter((r) => r.d <= radius).sort((p, q) => p.d - q.d);
  if (!reach.length) return { refuse: `OUT OF RANGE · ${name} ${radius} MU` };
  const s = Math.round(reach[0].d / speed);
  const eta = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const n = reach.length > 1 ? ` ×${reach.length}` : '';
  const what = mission === 'strike' ? `BOMBER${n} OUTBOUND · DROP IN ${eta}` : mission === 'patrol' ? `FIGHTER${n} ON STATION IN ${eta}` : `FIGHTER${n} SCOUTING · OVER TARGET IN ${eta}`;
  return { intents: reach.map((r) => ({ type: 'air', baseId: r.b.id, kind, mission, x, y })), confirm: what };
}

/**
 * Our launchers that can strike (x, y) now or after a reload: finished silos with an ICBM, subs
 * with an SLBM in range. For Q STRIKE on a rival's critical capital.
 */
export function launchersFor(view: PlayerView, x: number, y: number): EntityView[] {
  return [...view.entities.values()].filter(
    (e) =>
      e.owner === view.me &&
      e.state !== 'building' &&
      (e.detail?.loaded ?? 0) > 0 &&
      (e.kind === 'silo' || (e.kind === 'sub' && inSlbmRange(e, view.tick, x, y))),
  );
}

/** A rival's critical capital (§1.11) we can strike: one more hit there eliminates them. */
export interface StrikeOffer {
  player: number;
  /** its capital hex and centre (mu) */
  hex: HexIdx;
  x: number;
  y: number;
  /** the window closes at this tick */
  until: number;
  /** our launchers that reach it */
  launchers: EntityView[];
}

/**
 * The rival critical capital to offer for a strike: the one whose window closes first among those
 * our launchers reach (null: none, or weapons not released). `capitalOf` / `out` / `friendly` from the store.
 */
export function strikeOffer(
  view: PlayerView,
  world: WorldMap,
  capitalOf: (p: number) => HexIdx,
  friendly: (p: number) => boolean,
): StrikeOffer | null {
  if (!view.released || view.eliminated) return null;
  let best: StrikeOffer | null = null;
  for (const [p, until] of view.criticalCapitals) {
    if (p === view.me || friendly(p) || until <= view.tick || (best && until >= best.until)) continue;
    const hex = capitalOf(p);
    if (hex < 0) continue;
    const launchers = launchersFor(view, world.cx[hex], world.cy[hex]);
    if (launchers.length) best = { player: p, hex, x: world.cx[hex], y: world.cy[hex], until, launchers };
  }
  return best;
}
